import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useAsu } from "@framework/provider-asu";
import { useUser } from "@framework/provider-user";
import { IUser, UserVersion } from "@framework/types";

import { useStructureTreeSelection } from "../pages/dashboard/tabs/multi-analysis/hooks/use-structure-tree-selection";
import { useTrainPointsCache } from "../pages/dashboard/tabs/multi-analysis/hooks/use-train-points-cache";
import {
  DEFAULT_CELL_DISPLAY_CASCADE_3D,
  DEFAULT_TOOLBAR_STATE,
  buildCellDisplayCascade3DKey,
  type IMultiAnalysisActiveMeasureWindow,
  type IMultiAnalysisDataMark,
  type IMultiAnalysisTimeRange,
  type IMultiAnalysisToolbarState,
  type IStructureTreePoint,
  type MULTI_ANALYSIS_MEASURE_TYPE,
} from "../pages/dashboard/tabs/multi-analysis/types";
import {
  filterDataMarksInRange,
  findLastDataMarkInRange,
} from "../pages/dashboard/tabs/multi-analysis/utils/data-marks";
import type { SpeedScreenForm } from "../pages/dashboard/tabs/speed/hooks/use-speed-screen-forms";
import {
  AnalysisToolsProvider,
  useOptionalAnalysisTools,
  type IAnalysisToolsContextValue,
} from "./analysis-tools-context";

interface ISpeedContextValue extends IAnalysisToolsContextValue {
  selectedPoints: IStructureTreePoint[];
  frameRange: IMultiAnalysisTimeRange;
  setFrameRange: (range: IMultiAnalysisTimeRange) => void;
  dataMarks: IMultiAnalysisDataMark[];
  dataMarksError: boolean;
  isDataMarksLoading: boolean;
  setDataMarks: Dispatch<SetStateAction<IMultiAnalysisDataMark[]>>;
  setDataMarksError: (hasError: boolean) => void;
  setDataMarksLoading: (isLoading: boolean) => void;
  selectedDataMarks: IMultiAnalysisDataMark[];
  setSelectedDataMarks: Dispatch<SetStateAction<IMultiAnalysisDataMark[]>>;
  effectiveDataMarks: IMultiAnalysisDataMark[];
  setActiveMeasureType: (measureType: MULTI_ANALYSIS_MEASURE_TYPE | null) => void;
  setActiveMeasureWindow: (measureWindow: IMultiAnalysisActiveMeasureWindow | null) => void;
}

const SpeedContext = createContext<ISpeedContextValue | null>(null);

interface ISpeedScreenFormData {
  dataMarks: IMultiAnalysisDataMark[];
  dataMarksError: boolean;
  isDataMarksLoading: boolean;
  selectedDataMarks: IMultiAnalysisDataMark[];
}

type SpeedScreenFormDataById = Partial<Record<SpeedScreenForm, ISpeedScreenFormData>>;

interface ISpeedScreenFormDataContextValue {
  dataByFormId: SpeedScreenFormDataById;
  updateFormData: (formId: SpeedScreenForm, update: (previous: ISpeedScreenFormData) => ISpeedScreenFormData) => void;
}

const EMPTY_SCREEN_FORM_DATA: ISpeedScreenFormData = {
  dataMarks: [],
  dataMarksError: false,
  isDataMarksLoading: false,
  selectedDataMarks: [],
};
const EMPTY_SCREEN_FORM_DATA_BY_ID: SpeedScreenFormDataById = {};
const SpeedScreenFormDataContext = createContext<ISpeedScreenFormDataContextValue | null>(null);

export const SpeedProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { tab, selectedItem, startTimestamp, endTimestamp } = useAsu();
  const { profile } = useUser<IUser>();
  const inheritedAnalysisTools = useOptionalAnalysisTools();
  const isActive = tab === "7" && profile?.version === UserVersion.PROFESSIONAL;
  const selectedTrainId = selectedItem && "TrainId" in selectedItem ? Number(selectedItem.TrainId) : null;
  const {
    selectedPointIds,
    selectedPoints: allSelectedPoints,
    togglePoint,
    setTrainPointsSelected,
    beginTrainPointsSelection,
    resetSelection,
  } = useStructureTreeSelection({ isActive, selectedTrainId });
  const { pointsByTrainId, loadingTrainIds, loadTrainPoints } = useTrainPointsCache();
  const [activeMeasureType, setActiveMeasureType] = useState<MULTI_ANALYSIS_MEASURE_TYPE | null>(null);
  const [activeMeasureWindow, setActiveMeasureWindow] = useState<IMultiAnalysisActiveMeasureWindow | null>(null);
  const [toolbarState, setToolbarStateInternal] = useState<IMultiAnalysisToolbarState>(DEFAULT_TOOLBAR_STATE);
  const [cellDisplayCascade3DByKey, setCellDisplayCascade3DByKey] = useState<Record<string, boolean>>({});
  const [frameRange, setFrameRange] = useState<IMultiAnalysisTimeRange>({ start: startTimestamp, end: endTimestamp });
  const [dataMarks, setDataMarks] = useState<IMultiAnalysisDataMark[]>([]);
  const [dataMarksError, setDataMarksError] = useState(false);
  const [selectedDataMarks, setSelectedDataMarks] = useState<IMultiAnalysisDataMark[]>([]);
  const sourceKey = useMemo(
    () => ({ isActive, selectedTrainId, startTimestamp, endTimestamp }),
    [isActive, selectedTrainId, startTimestamp, endTimestamp],
  );
  const sourceKeyRef = useRef(sourceKey);
  sourceKeyRef.current = sourceKey;
  const [loadingState, setLoadingState] = useState<{ sourceKey: typeof sourceKey; isLoading: boolean }>(() => ({
    sourceKey,
    isLoading: false,
  }));
  const isDataMarksLoading = loadingState.sourceKey === sourceKey && loadingState.isLoading;
  const setDataMarksLoading = useCallback(
    (isLoading: boolean) => {
      if (sourceKeyRef.current !== sourceKey) return;
      setLoadingState(previous =>
        previous.sourceKey === sourceKey && previous.isLoading === isLoading ? previous : { sourceKey, isLoading },
      );
    },
    [sourceKey],
  );
  const [screenFormState, setScreenFormState] = useState<{
    sourceKey: typeof sourceKey;
    dataByFormId: SpeedScreenFormDataById;
  }>(() => ({ sourceKey, dataByFormId: EMPTY_SCREEN_FORM_DATA_BY_ID }));
  const dataByFormId =
    screenFormState.sourceKey === sourceKey ? screenFormState.dataByFormId : EMPTY_SCREEN_FORM_DATA_BY_ID;
  const availableMarkKeysByFormId = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(dataByFormId).map(([formId, data]) => [
          formId,
          { dataMarks: data.dataMarks, keys: new Set(data.dataMarks.map(mark => mark.key)) },
        ]),
      ) as Partial<Record<SpeedScreenForm, { dataMarks: IMultiAnalysisDataMark[]; keys: ReadonlySet<string> }>>,
    [dataByFormId],
  );
  const selectedPoints = useMemo(
    () => allSelectedPoints.filter(point => point.trainId === selectedTrainId),
    [allSelectedPoints, selectedTrainId],
  );

  useEffect(() => {
    resetSelection();
    setActiveMeasureType(null);
    setActiveMeasureWindow(null);
    setToolbarStateInternal(DEFAULT_TOOLBAR_STATE);
    setCellDisplayCascade3DByKey({});
  }, [isActive, resetSelection]);

  useEffect(() => {
    setFrameRange({ start: startTimestamp, end: endTimestamp });
    setDataMarks([]);
    setDataMarksError(false);
    setSelectedDataMarks([]);
  }, [isActive, selectedTrainId, startTimestamp, endTimestamp]);

  useEffect(() => {
    setScreenFormState(previous =>
      previous.sourceKey === sourceKey ? previous : { sourceKey, dataByFormId: EMPTY_SCREEN_FORM_DATA_BY_ID },
    );
  }, [sourceKey]);

  useEffect(() => {
    setScreenFormState(previous => {
      if (previous.sourceKey !== sourceKey || sourceKeyRef.current !== sourceKey) return previous;
      let reconciledDataByFormId: SpeedScreenFormDataById | null = null;
      for (const formId of Object.keys(previous.dataByFormId) as SpeedScreenForm[]) {
        const data = previous.dataByFormId[formId];
        if (!data?.selectedDataMarks.length) continue;
        const available = availableMarkKeysByFormId[formId];
        const keys =
          available?.dataMarks === data.dataMarks ? available.keys : new Set(data.dataMarks.map(mark => mark.key));
        const selectedDataMarks = filterDataMarksInRange(
          data.selectedDataMarks.filter(mark => keys.has(mark.key)),
          frameRange,
        );
        if (selectedDataMarks.length === data.selectedDataMarks.length) continue;
        reconciledDataByFormId ??= { ...previous.dataByFormId };
        reconciledDataByFormId[formId] = { ...data, selectedDataMarks };
      }
      return reconciledDataByFormId ? { ...previous, dataByFormId: reconciledDataByFormId } : previous;
    });
  }, [availableMarkKeysByFormId, frameRange, sourceKey]);

  const updateFormData = useCallback(
    (formId: SpeedScreenForm, update: (previous: ISpeedScreenFormData) => ISpeedScreenFormData) => {
      if (sourceKeyRef.current !== sourceKey) return;

      setScreenFormState(previous => {
        const previousDataByFormId =
          previous.sourceKey === sourceKey ? previous.dataByFormId : EMPTY_SCREEN_FORM_DATA_BY_ID;
        const previousData = previousDataByFormId[formId] ?? EMPTY_SCREEN_FORM_DATA;
        const nextData = update(previousData);
        if (nextData === previousData && previous.sourceKey === sourceKey) return previous;

        return { sourceKey, dataByFormId: { ...previousDataByFormId, [formId]: nextData } };
      });
    },
    [sourceKey],
  );
  const screenFormDataValue = useMemo(() => ({ dataByFormId, updateFormData }), [dataByFormId, updateFormData]);

  const effectiveDataMarks = useMemo(() => {
    if (selectedDataMarks.length) {
      return selectedDataMarks;
    }

    const lastMark = findLastDataMarkInRange(dataMarks, frameRange);
    return lastMark ? [lastMark] : [];
  }, [dataMarks, frameRange, selectedDataMarks]);

  const setToolbarState = useCallback((patch: Partial<IMultiAnalysisToolbarState>) => {
    setToolbarStateInternal(previous => ({ ...previous, ...patch }));
  }, []);

  const getCellDisplayCascade3D = useCallback(
    (templateId: number, cellId: string) =>
      cellDisplayCascade3DByKey[buildCellDisplayCascade3DKey(templateId, cellId)] ?? DEFAULT_CELL_DISPLAY_CASCADE_3D,
    [cellDisplayCascade3DByKey],
  );

  const setCellDisplayCascade3D = useCallback((templateId: number, cellId: string, display3D: boolean) => {
    setCellDisplayCascade3DByKey(previous => ({
      ...previous,
      [buildCellDisplayCascade3DKey(templateId, cellId)]: display3D,
    }));
  }, []);

  const value = useMemo<ISpeedContextValue>(
    () => ({
      isActive,
      selectedPointIds,
      selectedPoints,
      frameRange,
      setFrameRange,
      dataMarks,
      dataMarksError,
      isDataMarksLoading,
      setDataMarks,
      setDataMarksError,
      setDataMarksLoading,
      selectedDataMarks,
      setSelectedDataMarks,
      effectiveDataMarks,
      togglePoint,
      setTrainPointsSelected,
      beginTrainPointsSelection,
      pointsByTrainId,
      loadingTrainIds,
      loadTrainPoints,
      activeMeasureType,
      setActiveMeasureType,
      activeMeasureWindow,
      setActiveMeasureWindow,
      toolbarState,
      setToolbarState,
      getCellDisplayCascade3D,
      setCellDisplayCascade3D,
    }),
    [
      isActive,
      selectedPointIds,
      selectedPoints,
      frameRange,
      dataMarks,
      dataMarksError,
      isDataMarksLoading,
      setDataMarksLoading,
      selectedDataMarks,
      effectiveDataMarks,
      togglePoint,
      setTrainPointsSelected,
      beginTrainPointsSelection,
      pointsByTrainId,
      loadingTrainIds,
      loadTrainPoints,
      activeMeasureType,
      activeMeasureWindow,
      toolbarState,
      setToolbarState,
      getCellDisplayCascade3D,
      setCellDisplayCascade3D,
    ],
  );

  return (
    <SpeedContext.Provider value={isActive ? value : null}>
      <SpeedScreenFormDataContext.Provider value={isActive ? screenFormDataValue : null}>
        <AnalysisToolsProvider value={isActive ? value : inheritedAnalysisTools}>{children}</AnalysisToolsProvider>
      </SpeedScreenFormDataContext.Provider>
    </SpeedContext.Provider>
  );
};

export const useSpeed = (): ISpeedContextValue => {
  const context = useContext(SpeedContext);

  if (!context) {
    throw new Error("useSpeed must be used within SpeedProvider");
  }

  return context;
};

export const SpeedScreenFormProvider: FC<{ formId: SpeedScreenForm; children?: ReactNode }> = ({
  formId,
  children,
}) => {
  const sharedValue = useSpeed();
  const dataContext = useContext(SpeedScreenFormDataContext);
  if (!dataContext) throw new Error("SpeedScreenFormProvider must be used within SpeedProvider");

  const { dataByFormId, updateFormData } = dataContext;
  const { dataMarks, dataMarksError, isDataMarksLoading, selectedDataMarks } =
    dataByFormId[formId] ?? EMPTY_SCREEN_FORM_DATA;
  const setDataMarks = useCallback<Dispatch<SetStateAction<IMultiAnalysisDataMark[]>>>(
    update =>
      updateFormData(formId, previous => {
        const nextMarks = typeof update === "function" ? update(previous.dataMarks) : update;
        return nextMarks === previous.dataMarks ? previous : { ...previous, dataMarks: nextMarks };
      }),
    [formId, updateFormData],
  );
  const setDataMarksError = useCallback(
    (hasError: boolean) =>
      updateFormData(formId, previous =>
        hasError === previous.dataMarksError ? previous : { ...previous, dataMarksError: hasError },
      ),
    [formId, updateFormData],
  );
  const setSelectedDataMarks = useCallback<Dispatch<SetStateAction<IMultiAnalysisDataMark[]>>>(
    update =>
      updateFormData(formId, previous => {
        const nextMarks = typeof update === "function" ? update(previous.selectedDataMarks) : update;
        return nextMarks === previous.selectedDataMarks ? previous : { ...previous, selectedDataMarks: nextMarks };
      }),
    [formId, updateFormData],
  );
  const setDataMarksLoading = useCallback(
    (isLoading: boolean) =>
      updateFormData(formId, previous =>
        isLoading === previous.isDataMarksLoading ? previous : { ...previous, isDataMarksLoading: isLoading },
      ),
    [formId, updateFormData],
  );
  const effectiveDataMarks = useMemo(() => {
    if (selectedDataMarks.length) return selectedDataMarks;
    const lastMark = findLastDataMarkInRange(dataMarks, sharedValue.frameRange);
    return lastMark ? [lastMark] : [];
  }, [dataMarks, selectedDataMarks, sharedValue.frameRange]);
  const value = useMemo<ISpeedContextValue>(
    () => ({
      ...sharedValue,
      dataMarks,
      dataMarksError,
      isDataMarksLoading,
      setDataMarks,
      setDataMarksError,
      setDataMarksLoading,
      selectedDataMarks,
      setSelectedDataMarks,
      effectiveDataMarks,
    }),
    [
      sharedValue,
      dataMarks,
      dataMarksError,
      isDataMarksLoading,
      setDataMarks,
      setDataMarksError,
      setDataMarksLoading,
      selectedDataMarks,
      setSelectedDataMarks,
      effectiveDataMarks,
    ],
  );

  return <SpeedContext.Provider value={value}>{children}</SpeedContext.Provider>;
};
